import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, GitBranch, Shield, DollarSign } from "lucide-react";
import { Pipeline } from "@/types/pipeline";

interface PipelineCardProps {
  pipeline: Pipeline;
  permissionsCount: number;
  onAssignPermissions: (pipeline: Pipeline) => void;
  onDelete: (pipeline: Pipeline) => void;
  onViewDetails: (pipeline: Pipeline) => void;
}

export function PipelineCard({
  pipeline,
  permissionsCount,
  onAssignPermissions,
  onDelete,
  onViewDetails,
}: PipelineCardProps) {
  const handleDelete = () => {
    onDelete(pipeline);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <GitBranch className="h-4 w-4 text-blue-600" />
            <CardTitle className="text-lg font-semibold">
              {pipeline.name}
            </CardTitle>
          </div>
          <Badge className="bg-blue-100 text-blue-800">
            {pipeline.is_default ? "Por Defecto" : "Personalizado"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600 line-clamp-2">
          {pipeline.description || "Sin descripción"}
        </p>

        <div className="flex items-center gap-2 text-sm text-slate-600 pt-4 mt-4 border-t">
          <DollarSign className="h-4 w-4 text-green-600" />
          <span className="font-medium">Meta:</span>
          <span className="font-bold text-slate-800">
            {(pipeline.income_objective || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm text-slate-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Shield className="h-4 w-4 mr-1" />
              <span>
                {permissionsCount}{" "}
                {permissionsCount === 1 ? "permiso" : "permisos"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(pipeline)}
            className="text-blue-600 hover:text-blue-700"
            title="Editar pipeline"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={pipeline.is_default}
            className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              pipeline.is_default
                ? "No se puede eliminar el pipeline por defecto"
                : "Eliminar pipeline"
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

